import { MicIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import { AudioDuration, Button, ConfirmDeleteDialog } from '../../../../components'
import { FileCellProps } from './FileCell'
import { useUpload } from './hooks/useUpload'
import { useCogfy } from '../../../../hooks'
import { useMutation } from '@tanstack/react-query'
import { UUID } from '@indigohive/cogfy-types'
import { t } from 'i18next'

export function FileCellMicVariant (props: FileCellProps) {
  const { property } = props

  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [fileToDeleteId, setFileToDeleteId] = useState<UUID | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const handleStart = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const recorder = new MediaRecorder(stream)
    recorder.start()
    setStartDate(new Date())
    recorder.ondataavailable = (event) => {
      const mediaType = recorder.mimeType || event.data.type

      const extension = mediaType.split(';')[0].split('/')[1]
      const blob = new Blob([event.data])
      const file = new File([blob], `audio.${extension}`, { type: mediaType })
      upload.mutate(file)
    }
    setMediaStream(stream)
    setMediaRecorder(recorder)
  }

  const handleStop = () => {
    mediaRecorder?.stop()
    setStartDate(null)
    setMediaStream(null)
    setMediaRecorder(null)
  }

  const cogfy = useCogfy()
  const upload = useUpload(cogfy, {
    data: props.field.data,
    collectionId: props.collectionId,
    fieldId: props.field.id,
    recordId: props.record.id,
    onProgress: setUploadProgress
  })

  const deleteFile = useMutation({
    mutationFn: async (id: UUID) => {
      await cogfy.deleteFile({ fileId: id })
    }
  })

  const isRecording = Boolean(mediaStream && mediaRecorder)
  const isFileUploading = upload.status === 'pending'
  const value = property?.file?.value

  return (
    <>
      {value?.uploadStatus === 'sent' && value.url && (
        <div className='flex items-center gap-2 group'>
          <audio
            controls
            src={value.url}
          />

          {!props.readonly && (
            <Button
              onClick={() => setFileToDeleteId(value?.id ?? null)}
              className='opacity-0 group-hover:opacity-100 transition-opacity duration-300'
              ghost
              square
              size="xs"
            >
              <TrashIcon size={16} />
            </Button>
          )}
        </div>
      )}
      {!value && isRecording && (
        <div className="flex gap-2 items-center">
          <Button
            ghost
            size="sm"
            onClick={handleStop}
          >
            <MicIcon size={20} />
            Stop
          </Button>
          <AudioDuration
            startDate={startDate}
            handleStop={handleStop}
          />
        </div>
      )}
      {!value && !isRecording && !isFileUploading && (
        <Button
          ghost
          size="sm"
          onClick={() => { handleStart() }}
        >
          <MicIcon size={20} />
          Start
        </Button>
      )}
      {isFileUploading &&
        <div className="flex">
          <span className="loading loading-spinner loading-xs"></span>
          {uploadProgress !== null && <span className="text-slate-500 ml-2">{uploadProgress} %</span>}
        </div>
      }

      {fileToDeleteId && (
        <ConfirmDeleteDialog
          title={`${t('Delete file')} '${value?.name}'?`}
          open={fileToDeleteId !== null}
          onConfirm={() => {
            if (fileToDeleteId) {
              deleteFile.mutate(fileToDeleteId)
              setFileToDeleteId(null)
            }
          }}
          onClose={() => {
            setFileToDeleteId(null)
          }}
        />
      )}
    </>
  )
}
